import { Schema } from "effect"
import { AgentKind, ModelsByAgent } from "./common"
import { ProfileId, RunnerId, Slug, TypeKey } from "./ids"
import { AvoidPlace } from "./placement"

/**
 * Decision 0118: the CLIs a route allows, "any of". `agents` (non-empty) wins over the single `agent`, which older
 * rules carry alone and which stays for compatibility (absent `agents` decodes as before; older readers drop the key).
 */
const RouteAgents = Schema.optionalKey(Schema.Array(AgentKind))

/** A task owner's execution choice; required project rules remain authoritative. */
export const RoutingChoice = Schema.Struct({
  mode: Schema.Literals(["required", "preferred"]),
  agent: Schema.NullOr(AgentKind),
  agents: RouteAgents,
  byAgent: ModelsByAgent,
  runnerId: Schema.optionalKey(RunnerId),
  profileId: Schema.optionalKey(ProfileId),
  /** Decision 0095: places this choice never uses, whatever the mode (an agent's `retry_item{avoid}`). */
  avoid: Schema.optionalKey(Schema.Array(AvoidPlace)),
  reason: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(600)),
})
export type RoutingChoice = typeof RoutingChoice.Type
export const ItemRouting = Schema.Struct({
  worker: Schema.optionalKey(RoutingChoice),
  validator: Schema.optionalKey(RoutingChoice),
})

/** Ordered project workflow rules. The first rule matching the run kind and item type applies. */
export const RoutingRule = Schema.Struct({
  key: Slug,
  label: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  kind: Schema.Literals(["turn", "worker", "validator"]),
  itemTypes: Schema.optionalKey(Schema.Array(TypeKey)),
  mode: Schema.Literals(["required", "preferred"]),
  agent: Schema.NullOr(AgentKind),
  agents: RouteAgents,
  byAgent: ModelsByAgent,
  /** Platform API model id, independent of native CLI aliases. Absent/null inherits the runtime's provider default. */
  apiModel: Schema.optionalKey(
    Schema.NullOr(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200))),
  ),
  runnerId: Schema.optionalKey(RunnerId),
  profileId: Schema.optionalKey(ProfileId),
})
export type RoutingRule = typeof RoutingRule.Type

export const RoutingPolicy = Schema.Struct({ rules: Schema.Array(RoutingRule) })
export type RoutingPolicy = typeof RoutingPolicy.Type

/** Decision 0118: the CLIs a rule or choice allows (`agents`, else the single `agent`); empty = any. */
export const routeAgents = (
  r: { readonly agent: AgentKind | null; readonly agents?: ReadonlyArray<AgentKind> } | null | undefined,
): ReadonlyArray<AgentKind> =>
  r?.agents && r.agents.length > 0 ? [...new Set(r.agents)] : r?.agent ? [r.agent] : []
